import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiRefreshCw, FiMusic, FiClock } = FiIcons;

const RadioPlayer = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [retryKey, setRetryKey] = useState(0);
  const [isOffAir, setIsOffAir] = useState(false);
  const playerRef = useRef(null);

  useEffect(() => {
    const checkSchedule = () => {
      const now = new Date();
      const cstOffset = -6;
      const utc = now.getTime() + (now.getTimezoneOffset() * 60000);
      const cstTime = new Date(utc + (3600000 * cstOffset));
      const hours = cstTime.getHours();
      
      // Functional logic remains: Midnight to 5:00 AM CST
      setIsOffAir(hours >= 0 && hours < 5);
    };

    checkSchedule();
    const interval = setInterval(checkSchedule, 60000);
    return () => clearInterval(interval);
  }, []);

  const handleLoad = () => {
    setIsLoading(false);
    setHasError(false);
  };

  const handleError = () => {
    setHasError(true);
    setIsLoading(false);
  };

  const handleRetry = () => {
    setHasError(false);
    setIsLoading(true);
    setRetryKey(prev => prev + 1);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="max-w-4xl mx-auto"
    >
      <div className="glass-effect rounded-2xl p-6 sm:p-8 glow-effect">
        <div className="text-center mb-6">
          <h2 className="text-3xl font-bold text-white text-glow">
            Live Stream
          </h2>
        </div>

        <div className="relative">
          <div className="absolute -inset-4 bg-gradient-to-r from-crimson-600 to-red-600 rounded-2xl blur opacity-20"></div>
          
          <div className="relative rounded-xl overflow-hidden shadow-2xl border border-white/10 min-h-[160px] bg-white/5 flex items-center justify-center">
            {isOffAir ? (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/40 backdrop-blur-sm z-30 p-6 text-center">
                <SafeIcon icon={FiClock} className="w-12 h-12 text-cream-400 mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">Broadcast Offline</h3>
                <p className="text-gray-300 text-sm max-w-xs">
                  Our live stream is currently offline. We look forward to having you join us again soon.
                </p>
              </div>
            ) : (
              <>
                {isLoading && !hasError && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-[#f8f9fa] z-10">
                    <div className="w-10 h-10 border-4 border-crimson-500 border-t-transparent rounded-full animate-spin mb-3"></div>
                    <p className="text-gray-600 text-sm font-medium animate-pulse">Connecting to stream...</p>
                  </div>
                )}

                {hasError && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/5 backdrop-blur-md z-20 p-4 text-center">
                    <p className="text-red-400 text-sm font-medium mb-4">
                      Stream temporarily unavailable. Please try again later.
                    </p>
                    <button 
                      onClick={handleRetry}
                      className="flex items-center space-x-2 bg-crimson-600 hover:bg-crimson-700 text-white px-6 py-2 rounded-full transition-all duration-200 transform hover:scale-105 active:scale-95 shadow-lg font-semibold text-sm"
                    >
                      <SafeIcon icon={FiRefreshCw} className="w-4 h-4" />
                      <span>Retry Connection</span>
                    </button>
                  </div>
                )}

                {!hasError && (
                  <iframe
                    key={retryKey}
                    ref={playerRef}
                    src="https://s35.radiolize.com/public/appsumo__lajd7s_91oukw/embed?theme=light"
                    frameBorder="0"
                    allowTransparency="true"
                    tabIndex="0"
                    style={{ width: '100%', minHeight: '160px', border: 0 }}
                    className={`w-full bg-white/5 transition-opacity duration-500 ${isLoading ? 'opacity-0' : 'opacity-100'}`}
                    title="Ligon Road Innovations LLC live radio stream player"
                    aria-label="Ligon Road Innovations LLC live radio stream player"
                    loading="lazy"
                    onLoad={handleLoad}
                    onError={handleError}
                  />
                )}
              </>
            )}
          </div>
        </div>

        <div className="mt-6 text-center">
          <div className={`inline-flex items-center space-x-2 px-4 py-2 rounded-full border ${isOffAir ? 'bg-amber-500/20 text-amber-400 border-amber-500/30' : 'bg-green-500/20 text-green-400 border-green-500/30'}`}>
            <div className={`w-2 h-2 rounded-full ${isOffAir ? 'bg-amber-400' : 'bg-green-400 animate-pulse'}`}></div>
            <span className="text-sm font-semibold tracking-wide uppercase">
              {isOffAir ? 'Offline' : 'Live On Air'}
            </span>
          </div>
          
          <div className="flex items-center justify-center space-x-2 mt-3 text-gray-400 text-sm font-medium">
            <SafeIcon icon={isOffAir ? FiClock : FiMusic} className={isOffAir ? '' : 'text-cream-400 animate-pulse'} />
            <span>
              {isOffAir ? 'Check back soon' : 'Now Playing: Live Broadcast'}
            </span>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default RadioPlayer;